"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_TABLE_MATCH_MAX_CANDIDATES = exports.APP_TABLE_MATCH_MIN_PERCENT = exports.AppDatabaseRequirementDecisionMapSchema = exports.AppDatabaseRequirementDecisionSchema = exports.AppDatabaseColumnDecisionMapSchema = exports.AppDatabaseColumnDecisionSchema = exports.AppBaseWorkspaceDatabaseRefSchema = exports.AppDatabaseRequirementSharingPolicySchema = void 0;
exports.matchAppTable = matchAppTable;
exports.proposeAppTables = proposeAppTables;
exports.fittingAppTable = fittingAppTable;
exports.firstNamedAppTableDecisions = firstNamedAppTableDecisions;
exports.toAppDatabaseRequirement = toAppDatabaseRequirement;
exports.toAppDatabaseRequirementTarget = toAppDatabaseRequirementTarget;
exports.toAppDatabaseRequirements = toAppDatabaseRequirements;
exports.resolveAppDatabaseRequirementMappings = resolveAppDatabaseRequirementMappings;
exports.explicitAppDatabaseMappingDecisions = explicitAppDatabaseMappingDecisions;
exports.isAppBaseWorkspaceDatabaseRef = isAppBaseWorkspaceDatabaseRef;
exports.isAppBaseTitleColumn = isAppBaseTitleColumn;
exports.isAppColumnTargetAllowed = isAppColumnTargetAllowed;
exports.proposeAppDatabaseColumnDecisions = proposeAppDatabaseColumnDecisions;
exports.assessAppDatabaseColumnMapping = assessAppDatabaseColumnMapping;
exports.appInstallIdentifierSlug = appInstallIdentifierSlug;
exports.makeAppMappedIdentifier = makeAppMappedIdentifier;
const zod_1 = require("zod");
const app_base_column_map_1 = require("./app-base-column-map");
const app_base_config_1 = require("./app-base-config");
const app_base_contract_1 = require("./app-base-contract");
exports.AppDatabaseRequirementSharingPolicySchema = zod_1.z.enum([
    'shared',
    'dedicated',
]);
/** A database anywhere in the workspace, addressed by its host doc. */
exports.AppBaseWorkspaceDatabaseRefSchema = zod_1.z
    .object({
    docId: zod_1.z.string().trim().min(1),
    databaseId: zod_1.z.string().trim().min(1),
})
    .strict();
/** What happens to one App column when the App uses a workspace database. */
exports.AppDatabaseColumnDecisionSchema = zod_1.z.discriminatedUnion('action', [
    zod_1.z
        .object({
        action: zod_1.z.literal('map_existing'),
        targetColumnId: zod_1.z.string().trim().min(1),
    })
        .strict(),
    zod_1.z.object({ action: zod_1.z.literal('create_column') }).strict(),
]);
/** App column id -> decision. */
exports.AppDatabaseColumnDecisionMapSchema = zod_1.z.record(zod_1.z.string().min(1), exports.AppDatabaseColumnDecisionSchema);
exports.AppDatabaseRequirementDecisionSchema = zod_1.z.discriminatedUnion('action', [
    zod_1.z.object({ action: zod_1.z.literal('create_new') }).strict(),
    zod_1.z
        .object({
        action: zod_1.z.literal('use_existing'),
        /** Active binding key of the target App, or a workspace database. */
        target: zod_1.z.union([zod_1.z.string().min(1), exports.AppBaseWorkspaceDatabaseRefSchema]),
        /** Workspace database only; absent = the server proposes a mapping. */
        columns: exports.AppDatabaseColumnDecisionMapSchema.optional(),
    })
        .strict(),
]);
exports.AppDatabaseRequirementDecisionMapSchema = zod_1.z.record(zod_1.z.string(), exports.AppDatabaseRequirementDecisionSchema);
/** From this percent on a workspace table is offered for a package table. */
exports.APP_TABLE_MATCH_MIN_PERCENT = 40;
/** At most this many workspace tables are compared in one preflight. */
exports.APP_TABLE_MATCH_MAX_CANDIDATES = 64;
/**
 * Rates one package table against one existing table. Columns the existing
 * table has beyond the package's do not count against the pair. The caller
 * ranks the pairs and decides which tables the user may use at all.
 */
function matchAppTable(table, existing, rate) {
    const rating = rate({
        name: table.title,
        fields: table.columns.map(column => ({
            path: column.id,
            type: column.type,
            displayName: column.name,
        })),
    }, {
        name: existing.title,
        columns: existing.columns.map(column => ({
            id: column.id,
            name: column.name,
            type: column.type,
        })),
    });
    const targetByAppColumn = new Map(rating.suggestions.map(suggestion => [
        suggestion.responseField,
        suggestion.targetColumnId,
    ]));
    const columns = [];
    const unassigned = [];
    for (const column of table.columns) {
        const targetColumnId = targetByAppColumn.get(column.id);
        if (targetColumnId === undefined)
            unassigned.push(column.id);
        else
            columns.push({ appColumnId: column.id, targetColumnId });
    }
    return {
        percent: rating.percent,
        same: rating.percent === 100 && unassigned.length === 0,
        candidate: rating.percent >= exports.APP_TABLE_MATCH_MIN_PERCENT,
        columns,
        unassigned,
    };
}
/**
 * The workspace tables offered for one package table: those the importer can
 * read and that fit from the lowest percent on, the best first.
 */
function proposeAppTables(table, existing, rate) {
    return existing
        .filter(database => database.readable)
        .flatMap(database => {
        const match = matchAppTable(table, { title: database.label, columns: database.columns }, rate);
        return match.candidate
            ? [
                {
                    docId: database.docId,
                    databaseId: database.databaseId,
                    label: database.label,
                    percent: match.percent,
                    same: match.same,
                    usedBy: [...database.usedBy],
                },
            ]
            : [];
    })
        .sort((left, right) => right.percent - left.percent ||
        left.label.localeCompare(right.label) ||
        left.databaseId.localeCompare(right.databaseId));
}
/** The offered workspace table that fits completely: its reuse is preselected. */
function fittingAppTable(proposals) {
    return proposals.find(proposal => proposal.same);
}
/**
 * Per App the table decisions it carries. Apps of one App Container that name
 * the same table share it, so only the first App naming it carries the
 * decision; the installation decides it for every later App from the
 * container.
 */
function firstNamedAppTableDecisions(appTables, decisions) {
    const decided = new Set();
    return appTables.map(tables => Object.fromEntries(tables.flatMap(({ sourceKey }) => {
        const decision = decisions[sourceKey];
        if (decided.has(sourceKey) || decision === undefined)
            return [];
        decided.add(sourceKey);
        return [[sourceKey, decision]];
    })));
}
function toRequirementShape(input) {
    const sourceKey = 'sourceKey' in input ? input.sourceKey : input.key;
    if (!sourceKey.trim()) {
        throw new Error('App database requirement sourceKey is required');
    }
    const role = app_base_config_1.AppBaseDataSourceRoleSchema.parse(input.role);
    const contentModel = app_base_config_1.AppBaseContentModelSchema.parse(input.contentModel);
    const semanticKey = (0, app_base_contract_1.createAppBaseDatabaseSemanticKey)({
        sourceKey,
        semanticKey: input.semanticKey,
    });
    const schemaFingerprint = input.schemaFingerprint;
    if (!schemaFingerprint?.trim()) {
        throw new Error(`App database requirement ${sourceKey} requires schemaFingerprint`);
    }
    const displayName = input.displayName?.trim() || sourceKey;
    const sharingPolicy = exports.AppDatabaseRequirementSharingPolicySchema.parse(input.sharingPolicy ?? 'shared');
    return {
        requirementKey: input.requirementKey ?? sourceKey,
        sourceKey,
        semanticKey,
        role,
        contentModel,
        schemaFingerprint,
        displayName,
        sharingPolicy,
    };
}
function toAppDatabaseRequirement(input) {
    return toRequirementShape(input);
}
function toAppDatabaseRequirementTarget(input) {
    return toRequirementShape(input);
}
function toAppDatabaseRequirements(inputs) {
    return inputs.map(toAppDatabaseRequirement);
}
function resolveAppDatabaseRequirementMappings(input) {
    const existingByKey = new Map(input.existingTargets.map(target => [target.sourceKey, target]));
    const sortedExistingTargets = [...input.existingTargets].sort((a, b) => a.sourceKey.localeCompare(b.sourceKey));
    const plannedSemanticTargets = new Map();
    const mappings = [];
    const usedTargetKeys = new Set([
        ...input.usedTargetKeys,
        ...existingByKey.keys(),
    ]);
    const allocateTargetKey = (sourceKey) => {
        const base = input.forceProvisionKeyMapping || usedTargetKeys.has(sourceKey)
            ? input.mapInsertedKey(sourceKey)
            : sourceKey;
        let target = base;
        let suffix = 2;
        while (usedTargetKeys.has(target))
            target = `${base}-${suffix++}`;
        usedTargetKeys.add(target);
        return target;
    };
    for (const requirement of input.requirements) {
        const decision = input.decisions?.[requirement.sourceKey];
        if (decision?.action === 'use_existing') {
            const requested = decision.target;
            if (isAppBaseWorkspaceDatabaseRef(requested)) {
                mappings.push(resolveWorkspaceDatabaseMapping({
                    requirement,
                    ref: requested,
                    columns: decision.columns,
                    context: input.workspaceDatabases,
                    allocateTargetKey,
                }));
                continue;
            }
            if (decision.columns) {
                mappings.push(createConflictMapping({
                    requirement,
                    targetKey: requested,
                    reason: 'invalid_reuse_target',
                }));
                continue;
            }
            const plannedTarget = plannedSemanticTargets.get(requirement.semanticKey);
            const requestedTarget = existingByKey.get(requested) ??
                (plannedTarget?.targetKey === requested
                    ? plannedRequirementToTarget(plannedTarget)
                    : undefined);
            if (requirement.sharingPolicy !== 'shared' ||
                !requestedTarget ||
                !isSameSemanticDatabase(requirement, requestedTarget) ||
                !isSchemaCompatible(requirement, requestedTarget)) {
                mappings.push(createConflictMapping({
                    requirement,
                    targetKey: requested,
                    target: requestedTarget,
                    reason: requirement.sharingPolicy !== 'shared'
                        ? 'sharing_policy'
                        : requestedTarget
                            ? 'incompatible_schema'
                            : 'invalid_reuse_target',
                }));
                continue;
            }
            mappings.push({
                source: requirement.sourceKey,
                target: requestedTarget.sourceKey,
                action: 'use_existing',
            });
            continue;
        }
        if (decision?.action === 'create_new') {
            const plannedSemanticTarget = requirement.sharingPolicy === 'shared'
                ? plannedSemanticTargets.get(requirement.semanticKey)
                : undefined;
            if (plannedSemanticTarget) {
                if (!isSchemaCompatible(requirement, plannedSemanticTarget.requirement)) {
                    mappings.push(createConflictMapping({
                        requirement,
                        targetKey: plannedSemanticTarget.targetKey,
                        target: plannedRequirementToTarget(plannedSemanticTarget),
                        reason: 'incompatible_schema',
                    }));
                    continue;
                }
                mappings.push({
                    source: requirement.sourceKey,
                    target: plannedSemanticTarget.targetKey,
                    action: 'use_existing',
                });
                continue;
            }
            const target = allocateTargetKey(requirement.sourceKey);
            if (requirement.sharingPolicy === 'shared') {
                plannedSemanticTargets.set(requirement.semanticKey, {
                    targetKey: target,
                    requirement,
                });
            }
            mappings.push({
                source: requirement.sourceKey,
                target,
                action: target === requirement.sourceKey ? 'insert' : 'rename',
            });
            continue;
        }
        if (requirement.sharingPolicy === 'shared') {
            // A same-named but incompatible database is not a user-facing conflict.
            // Reuse only a compatible shared target; otherwise allocate a separate
            // database without modifying existing schemas or rows. Explicit reuse
            // decisions above and inconsistent requirements within a block still fail.
            const existingExact = existingByKey.get(requirement.sourceKey);
            const isCompatibleTarget = (target) => isSameSemanticDatabase(requirement, target) &&
                isSchemaCompatible(requirement, target);
            const compatibleTarget = existingExact && isCompatibleTarget(existingExact)
                ? existingExact
                : sortedExistingTargets.find(isCompatibleTarget);
            if (compatibleTarget) {
                mappings.push({
                    source: requirement.sourceKey,
                    target: compatibleTarget.sourceKey,
                    action: 'use_existing',
                });
                continue;
            }
            const semanticKey = requirement.semanticKey;
            const plannedSemanticTarget = plannedSemanticTargets.get(semanticKey);
            if (plannedSemanticTarget) {
                if (!isSchemaCompatible(requirement, plannedSemanticTarget.requirement)) {
                    mappings.push(createConflictMapping({
                        requirement,
                        targetKey: plannedSemanticTarget.targetKey,
                        target: plannedRequirementToTarget(plannedSemanticTarget),
                        reason: 'incompatible_schema',
                    }));
                    continue;
                }
                mappings.push({
                    source: requirement.sourceKey,
                    target: plannedSemanticTarget.targetKey,
                    action: 'use_existing',
                });
                continue;
            }
        }
        const target = allocateTargetKey(requirement.sourceKey);
        if (requirement.sharingPolicy === 'shared') {
            plannedSemanticTargets.set(requirement.semanticKey, {
                targetKey: target,
                requirement,
            });
        }
        mappings.push({
            source: requirement.sourceKey,
            target,
            action: target === requirement.sourceKey ? 'insert' : 'rename',
        });
    }
    return mappings;
}
/**
 * The decision for every data source of an install plan, made explicit: what
 * the plan reuses stays reused, everything else is created new. A decision
 * already made for a data source stays as it is.
 */
function explicitAppDatabaseMappingDecisions(mappings, existing = {}) {
    const decisions = { ...existing };
    for (const mapping of mappings) {
        if (decisions[mapping.source])
            continue;
        if (mapping.action === 'use_existing' || mapping.action === 'reuse') {
            decisions[mapping.source] = {
                action: 'use_existing',
                target: mapping.target,
            };
            continue;
        }
        if (mapping.action === 'conflict' ||
            mapping.action === 'insert' ||
            mapping.action === 'rename' ||
            mapping.action === 'create_new' ||
            mapping.action === 'provision_required') {
            decisions[mapping.source] = { action: 'create_new' };
        }
    }
    return decisions;
}
function isAppBaseWorkspaceDatabaseRef(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    const record = value;
    return (typeof record.docId === 'string' &&
        record.docId.trim().length > 0 &&
        typeof record.databaseId === 'string' &&
        record.databaseId.trim().length > 0);
}
function isSameWorkspaceDatabase(a, b) {
    return a.docId === b.docId && a.databaseId === b.databaseId;
}
const TITLE_COLUMN_TYPE = 'title';
/** The table's title column; an App never creates one. */
function isAppBaseTitleColumn(column) {
    return column.type === TITLE_COLUMN_TYPE;
}
/** Title columns map only onto each other. */
function isAppColumnTargetAllowed(appColumn, targetColumn) {
    return isAppBaseTitleColumn(appColumn) === isAppBaseTitleColumn(targetColumn);
}
/**
 * Server proposal for an App on a workspace database: the App title column
 * takes the table's title column, the others follow the type-aware matcher,
 * every unmatched App column gets a new column.
 */
function proposeAppDatabaseColumnDecisions(input) {
    const targetTitle = input.targetColumns.find(isAppBaseTitleColumn);
    const appTitle = input.appColumns.find(isAppBaseTitleColumn);
    const proposed = new Map(input.matcher
        .propose(input.appColumns.filter(column => !isAppBaseTitleColumn(column)), input.targetColumns.filter(column => !isAppBaseTitleColumn(column)))
        .map(match => [match.appColumnId, match.targetColumnId]));
    if (appTitle && targetTitle)
        proposed.set(appTitle.id, targetTitle.id);
    return Object.fromEntries(input.appColumns.map(column => {
        const targetColumnId = proposed.get(column.id);
        return [
            column.id,
            targetColumnId
                ? { action: 'map_existing', targetColumnId }
                : { action: 'create_column' },
        ];
    }));
}
/**
 * Validates a column mapping: every App column is mapped or created, no
 * target column is used twice, mapped types fit, the App title column takes
 * a title column, and columns are only created where the user may.
 */
function assessAppDatabaseColumnMapping(input) {
    const targetById = new Map(input.database.columns.map(column => [column.id, column]));
    const appIds = new Set(input.appColumns.map(column => column.id));
    const issues = Object.keys(input.decisions)
        .filter(columnId => !appIds.has(columnId))
        .map(columnId => ({
        reason: 'unknown_column',
        columnId,
        columnName: columnId,
    }));
    const usedTargets = new Set();
    const columns = input.appColumns.map((appColumn) => {
        const decision = input.decisions[appColumn.id] ?? null;
        const issue = (reason, target) => issues.push({
            reason,
            columnId: appColumn.id,
            columnName: appColumn.name,
            ...(target ? { targetColumnName: target.name } : {}),
        });
        if (!decision) {
            issue('unmapped');
            return { ...appColumn, decision, confidence: null };
        }
        if (decision.action === 'create_column') {
            if (isAppBaseTitleColumn(appColumn))
                issue('type_incompatible');
            else if (!input.database.canAddColumns)
                issue('create_forbidden');
            return { ...appColumn, decision, confidence: null };
        }
        const target = targetById.get(decision.targetColumnId);
        if (!target) {
            issue('target_missing');
            return { ...appColumn, decision, confidence: null };
        }
        if (usedTargets.has(target.id))
            issue('target_taken', target);
        usedTargets.add(target.id);
        const confidence = isAppColumnTargetAllowed(appColumn, target)
            ? input.matcher.rate(appColumn, target)
            : null;
        if (!confidence)
            issue('type_incompatible', target);
        return { ...appColumn, decision, confidence };
    });
    return { columns, issues };
}
/**
 * The mapping of a database the target App already binds: App columns
 * resolve through that binding, so a client decision cannot change it. A new
 * version of the bound App itself starts from that mapping and creates the
 * columns it adds, unless its decisions say otherwise.
 */
function assessBoundDatabaseColumns(input) {
    const exposed = new Set(input.binding.exposure.columnIds);
    const fixed = Object.fromEntries(input.appColumns.flatMap(column => {
        const targetColumnId = (0, app_base_column_map_1.toTargetColumnId)(input.binding, column.id);
        return exposed.has(targetColumnId)
            ? [[column.id, { action: 'map_existing', targetColumnId }]]
            : [];
    }));
    if (input.ofReplacedApp)
        return assessAppDatabaseColumnMapping({
            appColumns: input.appColumns,
            database: input.database,
            decisions: input.decisions ??
                Object.fromEntries(input.appColumns.map(column => [
                    column.id,
                    fixed[column.id] ?? { action: 'create_column' },
                ])),
            matcher: input.matcher,
        });
    const assessed = assessAppDatabaseColumnMapping({
        appColumns: input.appColumns,
        database: { columns: input.database.columns, canAddColumns: false },
        decisions: fixed,
        matcher: input.matcher,
    });
    const changed = Object.entries(input.decisions ?? {}).flatMap(([columnId, decision]) => (0, app_base_contract_1.stableJsonStringify)(decision) === (0, app_base_contract_1.stableJsonStringify)(fixed[columnId])
        ? []
        : [
            {
                reason: 'binding_fixed',
                columnId,
                columnName: input.appColumns.find(column => column.id === columnId)?.name ??
                    columnId,
            },
        ]);
    return {
        columns: assessed.columns,
        issues: [...assessed.issues, ...changed],
    };
}
function resolveWorkspaceDatabaseMapping(input) {
    const { requirement, context } = input;
    const ref = { docId: input.ref.docId, databaseId: input.ref.databaseId };
    const database = context?.databases.find(candidate => isSameWorkspaceDatabase(candidate, ref));
    const conflict = (reason) => ({
        ...createConflictMapping({
            requirement,
            targetKey: database?.bound?.sourceKey ?? ref.databaseId,
            reason,
        }),
        database: ref,
    });
    if (!context || !database)
        return conflict('invalid_reuse_target');
    if (requirement.sharingPolicy !== 'shared')
        return conflict('sharing_policy');
    if (!database.readable)
        return conflict('not_readable');
    if (!database.bound &&
        !(0, app_base_config_1.isAttachableAppBaseDatabaseRole)(requirement.role, context.placement(requirement.sourceKey)))
        return conflict('role_not_attachable');
    const appColumns = context.appColumns(requirement.sourceKey);
    const { columns, issues } = database.bound
        ? assessBoundDatabaseColumns({
            appColumns,
            database,
            binding: database.bound.binding,
            ofReplacedApp: database.bound.ofReplacedApp,
            decisions: input.columns,
            matcher: context.matcher,
        })
        : assessAppDatabaseColumnMapping({
            appColumns,
            database,
            decisions: input.columns ??
                proposeAppDatabaseColumnDecisions({
                    appColumns,
                    targetColumns: database.columns,
                    matcher: context.matcher,
                }),
            matcher: context.matcher,
        });
    if (issues.length > 0)
        return { ...conflict('column_mapping'), columns, columnIssues: issues };
    if (database.bound) {
        return {
            source: requirement.sourceKey,
            target: database.bound.sourceKey,
            action: 'use_existing',
            database: ref,
            columns,
        };
    }
    return {
        source: requirement.sourceKey,
        target: input.allocateTargetKey(requirement.sourceKey),
        action: 'attach_existing',
        database: ref,
        columns,
    };
}
function plannedRequirementToTarget(input) {
    return {
        ...input.requirement,
        sourceKey: input.targetKey,
    };
}
function isSameSemanticDatabase(a, b) {
    return a.semanticKey === b.semanticKey;
}
function isSchemaCompatible(a, b) {
    return (0, app_base_contract_1.isAppBaseDatabaseSchemaCompatible)(a.schemaFingerprint, b.schemaFingerprint);
}
function createConflictMapping(input) {
    return {
        source: input.requirement.sourceKey,
        target: input.targetKey,
        action: 'conflict',
        conflictReason: input.reason,
        sourceRequirementKey: input.requirement.requirementKey,
        targetRequirementKey: input.target?.requirementKey ?? input.targetKey,
        sourceSemanticKey: input.requirement.semanticKey,
        targetSemanticKey: input.target?.semanticKey,
        sourceSchemaFingerprint: input.requirement.schemaFingerprint,
        targetSchemaFingerprint: input.target?.schemaFingerprint,
    };
}
function appInstallIdentifierSlug(value) {
    return value
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 63);
}
/** The identifier allocation used by App installation and the local StreamKit. */
function makeAppMappedIdentifier(scope, source) {
    return `${scope}-${appInstallIdentifierSlug(source)}`;
}
